import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"

export type DirectorySearchResultType = "user" | "group"

export type DirectorySearchResult = {
  oid: string
  displayName: string
  type: DirectorySearchResultType
  email?: string
  jobTitle?: string
  department?: string
  companyName?: string
  officeLocation?: string
  city?: string
}

/**
 * Whitelist of grouping attributes the backend accepts for $filter-based searches.
 * Matches `ALLOWED_GROUP_ATTRIBUTES` in `app/backend/msgraph/directory_search.py`.
 */
export type DirectoryGroupAttribute = "department" | "companyName" | "officeLocation" | "city"

/**
 * Read the value of a configured grouping attribute off a directory result.
 * Defaults to `department` when the attribute is unknown.
 */
export function getDirectoryGroupValue(
  result: DirectorySearchResult,
  attribute: string
): string | undefined {
  switch (attribute) {
    case "department":
      return result.department
    case "companyName":
      return result.companyName
    case "officeLocation":
      return result.officeLocation
    case "city":
      return result.city
    default:
      return result.department
  }
}

export type DirectorySearchResponse = {
  results: DirectorySearchResult[]
  query: string
  types: string[]
  /** Whether group search is enabled on the backend (may be disabled due to missing permissions) */
  groupSearchEnabled?: boolean
  /** Present when `group_paged` or `group_page_cursor` was used on `/graph/directory/search`. */
  groupNextPageCursor?: string | null
  groupPaged?: boolean
}

export type DirectoryResolveResponse = {
  results: DirectorySearchResult[]
}

export type SearchDirectoryParams = {
  query: string
  types?: ("users" | "groups")[]
  top?: number
  /** When true, sends admin=true to bypass group search restrictions */
  admin?: boolean
  /** Scope user results to transitive members of this Azure AD group. */
  scopeGroup?: string
  /** Drop guests and disabled accounts from the user results. */
  membersOnly?: boolean
}

export type ListByGroupAttributeParams = {
  /** Graph user attribute to filter on (must be in the backend whitelist). */
  attribute: string
  /** Prefix value to match against the attribute (uses startsWith on the backend). */
  value: string
  /**
   * Maximum users to return after server-side Graph pagination (default {@link DIRECTORY_GROUP_LIST_MAX_TOP}).
   * The backend follows `@odata.nextLink` until exhausted or this cap.
   */
  top?: number
  /** When true, sends admin=true to bypass group search restrictions. */
  admin?: boolean
  /** Scope user results to transitive members of this Azure AD group. */
  scopeGroup?: string
}

/** Align with `graph_directory_search` group_attribute `top` cap (max_results). */
export const DIRECTORY_GROUP_LIST_MAX_TOP = 15_000

/** First page size for paged department/city/company filter in the Local AI Lead dialog. */
export const FILTERED_GROUP_ATTRIBUTE_PAGE_SIZE = 100

export const DirectorySearchService = {
  /**
   * Search Azure AD directory for users and/or groups.
   */
  async searchDirectory({
    query,
    types = ["users", "groups"],
    top = 10,
    admin,
    scopeGroup,
    membersOnly,
  }: SearchDirectoryParams): Promise<DirectorySearchResult[]> {
    if (!query || query.trim().length < 2) {
      return []
    }

    try {
      logger.debug("DirectorySearchService.searchDirectory", { query, types, top })
      const params: Record<string, string> = {
        q: query.trim(),
        types: types.join(","),
        top: top.toString(),
      }
      if (admin) params.admin = "true"
      if (scopeGroup) params.scope_group = scopeGroup
      if (membersOnly) params.members_only = "true"
      const response = (await api.get("/graph/directory/search", {
        params,
      })) as DirectorySearchResponse

      logger.debug("DirectorySearchService.searchDirectory: response", {
        count: response.results.length,
      })
      return response.results
    } catch (error) {
      logger.error("DirectorySearchService.searchDirectory: failed", error as Error)
      throw error
    }
  },

  /**
   * List users whose {attribute} starts with {value} (backend uses Graph $filter).
   * Used by the Local AI Lead dialog to filter or bulk-add colleagues by department,
   * companyName, officeLocation, or city.
   */
  async listByGroupAttribute({
    attribute,
    value,
    top = DIRECTORY_GROUP_LIST_MAX_TOP,
    admin,
    scopeGroup,
  }: ListByGroupAttributeParams): Promise<DirectorySearchResult[]> {
    if (!attribute || !value || !value.trim()) {
      return []
    }

    try {
      logger.debug("DirectorySearchService.listByGroupAttribute", {
        attribute,
        value,
        top,
      })
      const params: Record<string, string> = {
        group_attribute: attribute,
        group_value: value.trim(),
        top: top.toString(),
      }
      if (admin) params.admin = "true"
      if (scopeGroup) params.scope_group = scopeGroup
      const response = (await api.get("/graph/directory/search", {
        params,
      })) as DirectorySearchResponse
      logger.debug("DirectorySearchService.listByGroupAttribute: response", {
        count: response.results.length,
      })
      return response.results
    } catch (error) {
      logger.error("DirectorySearchService.listByGroupAttribute: failed", error as Error)
      throw error
    }
  },

  /**
   * Paged group-attribute listing: first page uses ``group_paged`` + ``top``; further pages
   * use ``group_page_cursor`` from the previous response.
   */
  async listByGroupAttributePage({
    attribute,
    value,
    pageSize = FILTERED_GROUP_ATTRIBUTE_PAGE_SIZE,
    cursor,
    admin,
    scopeGroup,
  }: {
    attribute: string
    value: string
    pageSize?: number
    cursor?: string | null
    admin?: boolean
    scopeGroup?: string
  }): Promise<{ results: DirectorySearchResult[]; nextPageCursor: string | null }> {
    if (!attribute || !value || !value.trim()) {
      return { results: [], nextPageCursor: null }
    }

    const params: Record<string, string> = {
      group_attribute: attribute,
      group_value: value.trim(),
    }
    if (cursor) {
      params.group_page_cursor = cursor
    } else {
      params.group_paged = "true"
      params.top = String(Math.min(999, Math.max(1, pageSize)))
    }
    if (admin) params.admin = "true"
    if (scopeGroup) params.scope_group = scopeGroup

    const response = (await api.get("/graph/directory/search", {
      params,
    })) as DirectorySearchResponse

    return {
      results: response.results,
      nextPageCursor: response.groupNextPageCursor ?? null,
    }
  },

  /**
   * Resolve directory object IDs into display objects (users/groups).
   *
   * Used when we only have stored OIDs (e.g., admin access policies) but want to
   * render people/groups the same way as the sharing UI.
   */
  async resolveDirectoryObjects(oids: string[]): Promise<DirectorySearchResult[]> {
    const unique = Array.from(new Set(oids.filter(Boolean)))
    if (unique.length === 0) return []

    try {
      const response = (await api.post("/graph/directory/resolve", {
        oids: unique,
      })) as DirectoryResolveResponse
      return response.results
    } catch (error) {
      logger.warn("DirectorySearchService.resolveDirectoryObjects: failed", error as Error)
      return []
    }
  },
}
